// src/components/Admin/DonorsTable.tsx

import React from "react";
import CustomTable, { Column } from "../Common/Table";
import Action from "../Common/Action"; // The array-based 3-dots menu
import type { EnhancedDonor } from "../../../types/donation";
import { Avatar, Box, Chip, Tooltip, useTheme, alpha } from "@mui/material";

// Icons
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import PaidIcon from "@mui/icons-material/Paid";
import VolunteerActivismOutlinedIcon from "@mui/icons-material/VolunteerActivismOutlined";

export interface DonorsTableProps {
  data: EnhancedDonor[];
  loading: boolean;
  page: number;
  rowsPerPage: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;

  // Required callbacks:
  onViewDonations: (donorId: string) => void;
  onEditDonor: (donorId: string) => void;
  onEmailDonor: (donorId: string) => void;
}

const DonorsTable: React.FC<DonorsTableProps> = ({
  data,
  loading,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onViewDonations,
  onEditDonor,
  onEmailDonor,
}) => {
  const theme = useTheme();
  
  // Define table columns
  const columns: Column<EnhancedDonor>[] = [
    { 
      label: "Donor", 
      key: "name",
      format: (value: string, row: EnhancedDonor) => {
        // Add null check for row and row.email
        const displayName = value || "Unknown";
        const email = row?.email || "";
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar 
              sx={{ 
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                mr: 2,
                width: 40, 
                height: 40
              }}
            >
              {displayName.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Box sx={{ fontWeight: 600 }}>{displayName}</Box>
              <Box sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{email}</Box>
            </Box>
          </Box>
        );
      }
    },
    { 
      label: "Email", 
      key: "email",
      format: (value: string) => {
        // Add null check for the email value
        if (!value) return <Box sx={{ color: 'text.secondary' }}>No email provided</Box>;
        
        return (
          <Tooltip title={`Send email to ${value}`} arrow>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': {
                  color: theme.palette.primary.main,
                }
              }}
              onClick={() => onEmailDonor(value)}
            >
              <EmailIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />
              <span>{value}</span>
            </Box>
          </Tooltip>
        );
      }
    },
    {
      label: "Total Donations",
      key: "totalDonations",
      format: (value: number) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PaidIcon 
            sx={{ 
              mr: 1, 
              color: theme.palette.success.main, 
              opacity: 0.8 
            }} 
          />
          <Box sx={{ fontWeight: 600, color: theme.palette.success.main }}>
            ${(value || 0).toLocaleString()}
          </Box>
        </Box>
      ),
    },
    { 
      label: "Donation Count", 
      key: "donationCount",
      format: (value: number) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <VolunteerActivismOutlinedIcon 
            sx={{ 
              mr: 1, 
              color: theme.palette.primary.main,
              opacity: 0.8 
            }} 
          />
          <Box sx={{ fontWeight: 500 }}>
            {value > 0 ? (
              <>
                <span>{value}</span>
                <Chip 
                  size="small" 
                  label={value > 5 ? "Frequent" : value > 1 ? "Repeat" : "First-time"} 
                  color={value > 5 ? "success" : value > 1 ? "primary" : "default"}
                  variant="outlined"
                  sx={{ ml: 1, height: 20 }}
                />
              </>
            ) : (
              <span>0</span>
            )}
          </Box>
        </Box>
      )
    },
  ];

  return (
    <CustomTable<EnhancedDonor>
      columns={columns}
      data={data}
      loading={loading}
      page={page}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      rowsPerPageOptions={[10, 25, 50, 100]}
      // For each donor row, we show a 3-dots Action menu with 3 items
      rowActions={(donor) => (
        <Action
          actions={[
            {
              label: "View Details",
              icon: <VisibilityIcon fontSize="small" />,
              onClick: () => onViewDonations(donor.id),
              color: theme.palette.info.main
            },
            {
              label: "Edit Donor",
              icon: <EditIcon fontSize="small" />,
              onClick: () => onEditDonor(donor.id),
              color: theme.palette.warning.main
            },
            {
              label: "Send Email",
              icon: <EmailIcon fontSize="small" />,
              onClick: () => {
                // Only call onEmailDonor if email exists
                if (donor.email) {
                  onEmailDonor(donor.email);
                }
              },
              color: theme.palette.success.main,
              // Disable the button if no email is available
              disabled: !donor.email
            },
          ]}
        />
      )}
    />
  );
};

export default DonorsTable;
